/**
 * addonImageUrl.js — normaliza la URL de una foto de ADDON para que SIEMPRE sea pintable.
 *
 * PROBLEMA (offline-v2): al reabrir una occurrence sincronizada, la foto-respuesta
 * (upload-edit-picture) se ve porque su `value` es una URL ABSOLUTA al CDN, pero la
 * foto-addon (item "pregunta + imagen") puede llegar como RUTA RELATIVA sin host
 * ("2026/06/xxx-item_addon_answer.jpeg") cuando el backend tiene CLOUDFRONT_URL_ACTUAL
 * vacío (Addon::getAnswerAttribute -> getThumbnail cae en `if(!$url) return $content`).
 * Una <img> con src relativo se resuelve contra el origin del SPA -> 404 -> se ve en BLANCO,
 * aunque el archivo esté a salvo en S3. La foto-respuesta NO sufre esto porque su URL ya es
 * absoluta. Este helper le da a la foto-addon la misma garantía: src absoluto.
 *
 * Reglas (mínimas, sin regresión):
 *   - vacío / no-string                -> se devuelve tal cual.
 *   - data: / blob: / file: / content: -> preview offline (base64/uri local), NO se toca.
 *   - http(s)://...                     -> ya absoluta (incluye la URL Thumbor de prod, que el
 *                                         CDN sí resuelve) -> NO se toca (preserva el thumbnail).
 *   - ruta relativa (sin host)         -> se antepone el CDN canónico de Binnacle.
 *
 * El CDN sale de CONFIG (`services.cloudfront.url` = env CLOUDFRONT_URL_ACTUAL, el MISMO env que usa el
 * backend en uploadFileToS3), con el CDN canónico de Binnacle como default cuando el env no está seteado.
 * Las rutas relativas solo ocurren cuando el backend no entrega host; ahí este default es la mejor opción.
 */

import { config } from 'src/config'

// CDN base desde config (sin slash final; se une con un solo '/'). Default = CDN canónico de Binnacle.
const DEFAULT_CDN = 'https://d17wedre8oulus.binnacle.mx'
const BINNACLE_CDN = (config('services.cloudfront.url', DEFAULT_CDN) || DEFAULT_CDN).replace(/\/+$/, '')

// Esquemas que representan un binario local / preview offline: NUNCA se reescriben a URL del CDN.
const LOCAL_OR_INLINE = /^(data:|blob:|file:|content:)/i

/**
 * @param {*} value  el `answer`/`thumbnail` crudo de un addon (URL, ruta relativa, base64, file uri…).
 * @returns {*} una URL pintable (absoluta) para imágenes con ruta relativa; el valor intacto en el resto.
 */
export function normalizeAddonImageUrl (value) {
  if (!value || typeof value !== 'string') return value
  if (LOCAL_OR_INLINE.test(value)) return value
  if (/^https?:\/\//i.test(value)) return value
  // ruta relativa sin host -> anteponer el CDN canónico (un solo slash de unión).
  return `${BINNACLE_CDN}/${value.replace(/^\/+/, '')}`
}

export default normalizeAddonImageUrl
